import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import envJson from "../env.json";
import { MENUITEMS } from "menuConstant/menu";
import { getMenuIconBySlug } from "menuConstant/menuIcons";
import { WEB_APP_VERSION, AdminRoleId } from "utils/constants";
import {
  isJustoSidebarCollapsed,
  setJustoSidebarCollapsed,
} from "utils/justoSidebar";
import { handleDetailResponse } from "utils/utils";
import "./SideNav.css";

const PUBLIC_URL = process.env.PUBLIC_URL || "";

const isMobileViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 991.98px)").matches;

const normalizePath = (path = "") => {
  if (!path) return "";
  const withBase = path.startsWith(PUBLIC_URL)
    ? path
    : `${PUBLIC_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const cleaned = withBase.replace(/\/+$/, "") || "/";
  return cleaned;
};

const pathMatches = (itemPath, currentPath) => {
  if (!itemPath) return false;
  const target = normalizePath(itemPath);
  const current = normalizePath(currentPath);
  return current === target || current.startsWith(`${target}/`);
};

const hasNavigablePath = (item) => {
  const path = item?.path;
  return (
    typeof path === "string" &&
    path.trim() !== "" &&
    path !== "undefined"
  );
};

const isConfiguredSidebarItem = (item) => {
  if (!item) return false;
  return MENUITEMS.some(
    (m) =>
      (item.slug && m.slug && item.slug === m.slug) ||
      (hasNavigablePath(item) && m.path === item.path) ||
      (!item.slug &&
        m.title &&
        item.title &&
        String(m.title).toLowerCase() === String(item.title).toLowerCase()),
  );
};

const itemOrChildMatches = (item, currentPath) => {
  if (pathMatches(item.path, currentPath)) return true;
  if (!item.children?.length) return false;
  return item.children.some((child) => itemOrChildMatches(child, currentPath));
};

const isIconComponent = (Icon) =>
  typeof Icon === "function" ||
  (typeof Icon === "object" && Icon !== null && (Icon.$$typeof || Icon.render));

const MENU_ICON_BY_KEY = MENUITEMS.reduce((map, item) => {
  if (!isIconComponent(item.icon)) return map;
  if (item.slug) map.set(`slug:${item.slug}`, item.icon);
  if (item.path) map.set(`path:${normalizePath(item.path)}`, item.icon);
  if (item.title) {
    map.set(`title:${String(item.title).toLowerCase()}`, item.icon);
  }
  return map;
}, new Map());

const resolveMenuIcon = (item) => {
  if (isIconComponent(item?.icon)) return item.icon;
  if (item?.slug) {
    const slugIcon = getMenuIconBySlug(item.slug);
    if (slugIcon) return slugIcon;
  }
  if (item?.slug && MENU_ICON_BY_KEY.has(`slug:${item.slug}`)) {
    return MENU_ICON_BY_KEY.get(`slug:${item.slug}`);
  }
  if (item?.path && MENU_ICON_BY_KEY.has(`path:${normalizePath(item.path)}`)) {
    return MENU_ICON_BY_KEY.get(`path:${normalizePath(item.path)}`);
  }
  if (item?.title) {
    const key = `title:${String(item.title).toLowerCase()}`;
    if (MENU_ICON_BY_KEY.has(key)) return MENU_ICON_BY_KEY.get(key);
  }
  return null;
};

const MenuIcon = ({ icon: Icon }) => {
  if (isIconComponent(Icon)) {
    return (
      <Icon
        className="justo-sidebar__icon"
        size={16}
        strokeWidth={1.75}
        aria-hidden="true"
      />
    );
  }
  return (
    <i
      className="fa fa-th-large justo-sidebar__icon justo-sidebar__icon--fa"
      aria-hidden="true"
    />
  );
};

const SideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mainMenu, setMainMenu] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [mobileOpen, setMobileOpen] = useState(false);
  const permissionSelector = useSelector((state) => state.permission);
  const { response } = permissionSelector;
  const loginSelector = useSelector((state) => state.login);
  const roleId = handleDetailResponse(loginSelector?.response?.data)?.role_id;
  const isSuperAdmin = String(roleId || "") === String(AdminRoleId);

  useEffect(() => {
    if (!response) return;
    const sorted = [...response]
      .map((item) => {
        const menuMatch = MENUITEMS.find(
          (m) =>
            (item.slug && m.slug === item.slug) ||
            (item.path && m.path === item.path) ||
            (item.title &&
              m.title &&
              String(m.title).toLowerCase() ===
                String(item.title).toLowerCase()),
        );
        // Only keep API children; do not inject MENUITEMS children for unmatched
        // page-level permission modules (that created empty/undefined nav links).
        let children =
          Array.isArray(item.children) && item.children.length > 0
            ? item.children.filter(hasNavigablePath)
            : undefined;

        // Keep Access Control as its own top-level item (not under Configuration).
        if (Array.isArray(children)) {
          children = children.filter(
            (child) =>
              !String(child.path || "").includes(
                "configurationDefaultPassword",
              ) && !String(child.path || "").includes("accessControl"),
          );
          if (children.length === 0) {
            children = undefined;
          }
        }

        // Do not treat Access Control as a normal permission-menu row from API;
        // it is injected once below for super admin only.
        if (
          String(item.path || "").includes("configurationDefaultPassword") ||
          String(item.path || "").includes("accessControl") ||
          item.slug === "access_control"
        ) {
          return null;
        }

        return {
          ...item,
          icon: resolveMenuIcon(item) || menuMatch?.icon || item.icon,
          path: hasNavigablePath(item) ? item.path : menuMatch?.path || item.path,
          children,
          type:
            children?.length > 0
              ? item.type === "sub" || menuMatch?.type === "sub"
                ? "sub"
                : item.type || "sub"
              : item.type || "link",
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.sequence - b.sequence);

    if (isSuperAdmin) {
      const already = sorted.some(
        (item) =>
          String(item.path || "").includes("accessControl") ||
          item.slug === "access_control",
      );
      if (!already) {
        const accessItem = {
          path: "/accessControl",
          title: "Access Control",
          type: "link",
          slug: "access_control",
          permission: true,
          deploy: true,
          active: false,
          icon: getMenuIconBySlug("access_control"),
        };
        // Keep existing API order; place next to Configuration (not at sequence 2).
        const configIdx = sorted.findIndex(
          (item) => item.slug === "configuration_management",
        );
        if (configIdx >= 0) {
          sorted.splice(configIdx + 1, 0, accessItem);
        } else {
          sorted.push(accessItem);
        }
      }
    }

    setMainMenu(sorted);
  }, [response, isSuperAdmin]);

  useEffect(() => {
    if (!mainMenu.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      mainMenu.forEach((item, index) => {
        if (
          item.children?.length &&
          itemOrChildMatches(item, location.pathname)
        ) {
          next.add(item.slug || item.path || String(index));
          item.children.forEach((child, childIndex) => {
            if (
              child.children?.length &&
              itemOrChildMatches(child, location.pathname)
            ) {
              next.add(
                child.slug || child.path || `${index}-${childIndex}`,
              );
            }
          });
        }
      });
      return next;
    });
  }, [location.pathname, mainMenu]);

  useEffect(() => {
    const syncMobileOpen = () => {
      setMobileOpen(isMobileViewport() && !isJustoSidebarCollapsed());
    };
    syncMobileOpen();
    window.addEventListener("justo-sidebar-toggle", syncMobileOpen);
    window.addEventListener("resize", syncMobileOpen);
    return () => {
      window.removeEventListener("justo-sidebar-toggle", syncMobileOpen);
      window.removeEventListener("resize", syncMobileOpen);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  const visibleMenu = useMemo(
    () =>
      mainMenu.filter(
        (menuItem) =>
          menuItem.permission &&
          (envJson?.ENV === "PROD" ? menuItem.deploy : true) &&
          isConfiguredSidebarItem(menuItem) &&
          (hasNavigablePath(menuItem) ||
            menuItem.children?.some((child) => hasNavigablePath(child))),
      ),
    [mainMenu],
  );

  const closeMobileSidebar = useCallback(() => {
    if (isMobileViewport()) {
      setJustoSidebarCollapsed(true);
      setMobileOpen(false);
    }
  }, []);

  const toggleExpanded = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const goHome = () => {
    navigate("/");
    closeMobileSidebar();
  };

  const renderLink = (item, { nested = false } = {}) => {
    if (!hasNavigablePath(item)) return null;
    const active = pathMatches(item.path, location.pathname);
    return (
      <Link
        to={`${PUBLIC_URL}${item.path}`}
        className={`sidebar-header${active ? " active" : ""}${
          nested ? " justo-sidebar__link--nested" : ""
        }`}
        aria-current={active ? "page" : undefined}
        onClick={closeMobileSidebar}
      >
        {!nested ? <MenuIcon icon={resolveMenuIcon(item)} /> : null}
        <span>{item.title}</span>
      </Link>
    );
  };

  const renderSubToggle = (item, id, isOpen) => (
    <button
      type="button"
      className={`sidebar-header justo-sidebar__toggle${
        isOpen ? " is-open" : ""
      }`}
      aria-expanded={isOpen}
      aria-controls={`justo-submenu-${id}`}
      onClick={() => toggleExpanded(id)}
    >
      <MenuIcon icon={resolveMenuIcon(item)} />
      <span>{item.title}</span>
      <i className="fa fa-angle-right pull-right" aria-hidden="true" />
    </button>
  );

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="justo-sidebar__backdrop"
          aria-label="Close navigation"
          onClick={closeMobileSidebar}
        />
      ) : null}
      <aside
        id="justo-main-sidebar"
        className="page-sidebar justo-sidebar"
        aria-label="Main navigation"
      >
        <div className="main-header-left d-none d-lg-block">
          <div className="justo-sidebar__header">
            <div className="logo-wrapper justo-sidebar__brand">
              <button
                type="button"
                className="justo-sidebar__logo-btn"
                onClick={goHome}
                aria-label="Go to dashboard"
              >
                <img
                  src="/assets/images/JUSTOLOGO.jpg"
                  alt="Justo"
                  className="justo-sidebar__logo"
                />
              </button>
            </div>
            <p className="justo-sidebar__version">
              <span className="justo-sidebar__version-label">Version</span>{" "}
              <span className="justo-sidebar__version-num">
                {WEB_APP_VERSION}
              </span>
            </p>
          </div>
        </div>

        <nav className="sidebar custom-scrollbar" aria-label="Modules">
          <ul className="sidebar-menu">
            {visibleMenu.map((menuItem, i) => {
              const itemId = menuItem.slug || menuItem.path || String(i);
              const isOpen = expandedIds.has(itemId);
              const sectionActive = itemOrChildMatches(
                menuItem,
                location.pathname,
              );

              return (
                <li
                  className={`${sectionActive ? "active" : ""}${
                    isOpen ? " is-expanded" : ""
                  }`}
                  key={itemId}
                >
                  {menuItem.sidebartitle ? (
                    <div className="sidebar-title">{menuItem.sidebartitle}</div>
                  ) : null}

                  {menuItem.children?.length
                    ? renderSubToggle(menuItem, itemId, isOpen)
                    : null}

                  {!menuItem.children?.length && hasNavigablePath(menuItem)
                    ? renderLink(menuItem)
                    : null}

                  {menuItem.children?.length ? (
                    <ul
                      id={`justo-submenu-${itemId}`}
                      className={`sidebar-submenu${
                        isOpen ? " menu-open" : ""
                      }`}
                      aria-hidden={!isOpen}
                    >
                      {menuItem.children.map((childrenItem, index) => {
                        const childId =
                          childrenItem.slug ||
                          childrenItem.path ||
                          `${itemId}-${index}`;
                        const childOpen = expandedIds.has(childId);
                        const childActive = itemOrChildMatches(
                          childrenItem,
                          location.pathname,
                        );

                        return (
                          <li
                            key={childId}
                            className={childActive ? "active" : ""}
                          >
                            {childrenItem.children?.length ? (
                              <button
                                type="button"
                                className={`justo-sidebar__sub-toggle${
                                  childOpen ? " is-open" : ""
                                }`}
                                aria-expanded={childOpen}
                                onClick={() => toggleExpanded(childId)}
                              >
                                <span>{childrenItem.title}</span>
                                <i
                                  className="fa fa-angle-right"
                                  aria-hidden="true"
                                />
                              </button>
                            ) : null}

                            {!childrenItem.children?.length &&
                            hasNavigablePath(childrenItem)
                              ? renderLink(childrenItem, { nested: true })
                              : null}

                            {childrenItem.children?.length ? (
                              <ul
                                className={`sidebar-submenu${
                                  childOpen ? " menu-open" : ""
                                }`}
                                aria-hidden={!childOpen}
                              >
                                {childrenItem.children.map(
                                  (childrenSubItem, key) => (
                                    <li
                                      className={
                                        pathMatches(
                                          childrenSubItem.path,
                                          location.pathname,
                                        )
                                          ? "active"
                                          : ""
                                      }
                                      key={
                                        childrenSubItem.path ||
                                        `${childId}-${key}`
                                      }
                                    >
                                      {childrenSubItem.path
                                        ? renderLink(childrenSubItem, {
                                            nested: true,
                                          })
                                        : null}
                                    </li>
                                  ),
                                )}
                              </ul>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default SideBar;
